import LanguageSync from './i18n/LanguageSync';
import { LanguageProvider } from './i18n/LanguageContext';
import VisitorDashboard from './pages/VisitorDashboard';
import InstallApp from './pages/InstallApp';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initAnalytics, trackPageview } from './utils/analytics';
import { loadMetaPixel, metaPageView } from './utils/metaPixel';
import Login from './pages/Login';
import Register from './pages/Register';
import CoachDashboard from './pages/CoachDashboard';
import SportifDashboard from './pages/SportifDashboard';
import Messaging from './pages/Messaging';
import LandingPage from './pages/LandingPage';
import LegalPage from './pages/LegalPage';
import CookieBanner from './components/CookieBanner';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import SubscriptionCheckout from './pages/SubscriptionCheckout';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import AdminRoleSwitcher from './components/AdminRoleSwitcher';
import ChatBot from './components/ChatBot';
import DemoBanner from './components/DemoBanner';
import AndroidBackButton from './components/AndroidBackButton';
import PushManager from './components/PushManager';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ColorModeProvider } from './context/ColorModeContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './components/ConfirmDialog';
import { CircularProgress, Box, CssBaseline } from '@mui/material';

interface PrivateRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

const PrivateRoute = ({ children, allowedRoles }: PrivateRouteProps) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user || user.is_demo) {
    return <Navigate to="/visiteur" replace />;
  }

  // Admin can access everything
  if (user.role === 'ADMIN') {
    return <>{children}</>;
  }

  const hasAccess = () => {
    if (!allowedRoles) return true;
    if (allowedRoles.includes(user.role)) return true;
    // Allow access if the user has the specific profile required
    if (allowedRoles.includes('COACH') && user.coach_profile) return true;
    if (allowedRoles.includes('SPORTIF') && user.sportif_profile) return true;
    return false;
  };

  if (allowedRoles && !hasAccess()) {
    // Redirect to their primary role's dashboard if access is denied
    return <Navigate to={user.role === 'COACH' ? '/coach/dashboard' : '/sportif/dashboard'} />;
  }

  return <>{children}</>;
};

const AuthChatBot = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <ChatBot />;
};

// Initialise la mesure d'audience (si configuree) et signale chaque changement
// de page — indispensable en application monopage, ou la navigation ne
// recharge pas le document.
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  // Charge le Meta Pixel si le consentement a deja ete accorde lors d'une visite
  // precedente (sinon il attend le clic « Accepter » du bandeau cookies).
  useEffect(() => { loadMetaPixel(); }, []);
  useEffect(() => {
    trackPageview(location.pathname + location.search);
    metaPageView();
  }, [location]);
  return null;
};

function App() {
  return (
    <LanguageProvider><ColorModeProvider>
      <CssBaseline />
      <ToastProvider>
      <ConfirmProvider>
      <Router>
        <AuthProvider>
          <LanguageSync/>
          <RouteTracker />
          <AndroidBackButton />
          <PushManager />
          <AdminRoleSwitcher />
          {/* Bandeau permanent signalant le mode découverte */}
          <DemoBanner />
          {/* Bandeau d'information cookies (une seule fois) */}
          <CookieBanner />
          {/* ChatBot visible uniquement après connexion */}
          <AuthChatBot />
          <Routes>
            <Route path="/visiteur" element={<VisitorDashboard />} />
            <Route path="/installer" element={<InstallApp />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirmer-email" element={<ConfirmEmail />} />
            <Route
              path="/coach/subscription"
              element={
                <PrivateRoute allowedRoles={['COACH']}>
                  <SubscriptionCheckout />
                </PrivateRoute>
              }
            />
            <Route
              path="/coach/subscription/success"
              element={
                <PrivateRoute allowedRoles={['COACH']}>
                  <SubscriptionSuccess />
                </PrivateRoute>
              }
            />
            <Route
              path="/coach/subscription/cancel"
              element={
                <PrivateRoute allowedRoles={['COACH']}>
                  <SubscriptionCancel />
                </PrivateRoute>
              }
            />

            <Route
              path="/coach/dashboard/*"
              element={
                <PrivateRoute allowedRoles={['COACH']}>
                  <CoachDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/sportif/dashboard/*"
              element={
                <PrivateRoute allowedRoles={['SPORTIF']}>
                  <SportifDashboard />
                </PrivateRoute>
              }
            />

            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <Messaging />
                </PrivateRoute>
              }
            />

            <Route path="/mentions-legales" element={<LegalPage slug="mentions-legales" />} />
            <Route path="/confidentialite" element={<LegalPage slug="confidentialite" />} />
            <Route path="/cgv" element={<LegalPage slug="cgv" />} />
            <Route path="/cookies" element={<LegalPage slug="cookies" />} />

            <Route path="/" element={<LandingPage />} />
          </Routes>
        </AuthProvider>
      </Router>
      </ConfirmProvider>
      </ToastProvider>
    </ColorModeProvider></LanguageProvider>
  );
}

export default App;

